import { useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import useOnboardingAiBuddy from '@wlb/shared/hooks/useOnboardingAiBuddy';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { FormError } from '../components/ui/FormError';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { OnboardingIllustration } from '../features/auth/OnboardingIllustration';
import { MATCH_ILLUSTRATIONS } from '../features/auth/onboardingIllustrations';
import './matchOnboarding.css';

export function AiBuddyOnboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { completeAuthSession, token } = useAuth();
  const { buddy, loading, assignFailed, error, retry } = useOnboardingAiBuddy();

  /** Leave the onboarding route before the session flips, so the shell mounts
   * around the feed rather than around an unmounting onboarding screen. */
  const continueToApp = async (): Promise<void> => {
    navigate('/', { replace: true });
    await completeAuthSession(token ?? undefined);
  };

  const renderCard = () => {
    if (loading) {
      return (
        <article className="match-onboarding__ai-card">
          <h2>{t('common.meetYourAiBuddy')}</h2>
          <SkeletonGroup label={t('common.loading')}>
            <div className="match-onboarding__ai-profile" aria-hidden>
              <Skeleton shape="circle" className="match-onboarding__ai-skeleton-avatar" />
              <div className="match-onboarding__ai-skeleton-meta">
                <Skeleton className="match-onboarding__ai-skeleton-line--name" />
                <Skeleton />
                <Skeleton />
              </div>
            </div>
          </SkeletonGroup>
        </article>
      );
    }

    if (!buddy) {
      return (
        <article className="match-onboarding__ai-card match-onboarding__ai-card--status">
          <p className="match-onboarding__ai-status">{error || t('aiBuddy.noMatch1')}</p>
          <Button size="sm" variant="outline" onClick={retry}>
            {t('common.buttons.retry')}
          </Button>
        </article>
      );
    }

    return (
      <article className="match-onboarding__ai-card">
        <h2>{t('common.meetYourAiBuddy')}</h2>
        <div className="match-onboarding__ai-profile">
          <Avatar src={buddy.image} name={buddy.name} size="lg" />
          <div>
            <strong>
              {buddy.name} {buddy.role && <small>{buddy.role}</small>}
            </strong>
            {buddy.description && <p>{buddy.description}</p>}
          </div>
        </div>
        <p className="match-onboarding__alignment">{t('aiBuddy.alignmentText', { buddyName: buddy.name })}</p>

        {assignFailed ? (
          <div className="match-onboarding__ai-retry">
            <FormError message={error || t('common.errors.generic')} />
            <Button size="sm" variant="outline" onClick={retry}>
              {t('common.buttons.retry')}
            </Button>
          </div>
        ) : (
          <small className="match-onboarding__info">
            ⓘ {t('aiBuddy.infoText', { buddyName: buddy.name })}
          </small>
        )}
      </article>
    );
  };

  return (
    <AuthFlowLayout>
      <section className="match-onboarding match-onboarding--ai-buddy">
        <button className="auth-flow__back" type="button" onClick={() => navigate('/onboarding/matches')} aria-label={t('common.buttons.back')}>‹</button>
        <OnboardingIllustration src={MATCH_ILLUSTRATIONS.aiBuddy} size="hero" />
        <h1>{t('common.robiAlsoFoundYouAGreatBuddy')}</h1>
        {renderCard()}
        <Button fullWidth size="lg" onClick={continueToApp}>{t('common.buttons.continueToApp')}</Button>
      </section>
    </AuthFlowLayout>
  );
}
